import { useAuth } from '@/client/hooks/use-auth';
import { useDidAuthStore } from '@/client/store/did-auth-store';
import { useModelStore } from '@/client/store/model-store';
import { api } from '@/client/utils/api';
import { useMe } from '@/libs/react-sdk/api/use-me';
import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * DID -> wallet bridge. A DID social login gives identity only (no signer), so
 * the user is signed in but cannot trade until they connect a wallet. This slim
 * banner makes that next step obvious instead of a silent dead-end at trade time.
 * Shows only when signed in via DID AND no wallet connected yet.
 *
 * Also auto-opens the onboarding wizard once per DID sub (keyed dismiss flag).
 */
export const ConnectWalletBridge = memo(() => {
  const { t } = useTranslation();
  const setOnboardingOpen = useModelStore(s => s.setOnboardingOpen);
  const identity = useDidAuthStore(s => s.identity);
  const { canTrade } = useAuth();
  const me = useMe(api, { enabled: !!identity });
  const dismissedFor = useDidAuthStore(s => s.onboardingDismissedFor);
  const autoOpenedRef = useRef<string | null>(null);

  // Gate on the PERSISTENT binding (canTrade), NOT walletConnected. A DID user who
  // already linked a wallet keeps canTrade across logins; the per-session wallet
  // CONNECTION dropping (e.g. after the DID OAuth full-page redirect) must NOT
  // re-trigger the "connect a wallet to set up" nag — that over-fire is what made a
  // bound user feel like they re-bind every login (ADR-051 D1). Wait for /me to
  // resolve (me.isSuccess) so a returning bound user never flashes the setup prompt
  // while canTrade is momentarily false during load.
  const needsWalletSetup = !!identity && me.isSuccess && !canTrade;

  // Auto-open the wizard once for a DID identity that hasn't linked a wallet yet
  // (not dismissed for this sub).
  useEffect(() => {
    const sub = identity?.sub;
    if (!sub || !needsWalletSetup) return;
    if (dismissedFor.includes(sub)) return;
    if (autoOpenedRef.current === sub) return; // don't re-pop within the session
    autoOpenedRef.current = sub;
    setOnboardingOpen(true);
  }, [identity?.sub, needsWalletSetup, dismissedFor, setOnboardingOpen]);

  if (!needsWalletSetup) return null;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pt-3">
      <div className="flex items-center gap-3 rounded-xl border border-border-bright bg-primary/8 px-4 py-2.5 shadow-card-glow">
        <span className="i-svg-spinners:90-ring-with-bg hidden" aria-hidden />
        <span className="shrink-0 size-7 rounded-lg bg-primary/15 flex items-center justify-center">
          <svg
            viewBox="0 0 24 24"
            className="size-4 text-primary"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
            <path d="M16 12h.01M3 9h18" />
          </svg>
        </span>
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-medium text-foreground truncate">{t('bridge-signed-in', 'Signed in with DID')}</span>
          <span className="text-xs text-muted-foreground truncate">
            {t('bridge-connect-to-trade', 'Connect a wallet to start trading')}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOnboardingOpen(true)}
          className="btn-brand btn-brand shrink-0 px-4 py-2 rounded-lg text-sm whitespace-nowrap"
        >
          {t('finish-setup', 'Finish setup')}
        </button>
      </div>
    </div>
  );
});

ConnectWalletBridge.displayName = 'ConnectWalletBridge';
